import type {
  Locale,
  ObligationActionKindContract,
  ObligationActionResultContract,
  ObligationContract,
  ObligationFamilyContract,
  StudentProfileContract,
  StudentSearchItemContract,
} from "@chams-pay/contracts";
import { normalizeSchoolLevelLabel, operationsMessages, translate } from "@chams-pay/contracts";
import { Badge } from "@chams-pay/design-system/components/badge";
import { Button } from "@chams-pay/design-system/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@chams-pay/design-system/components/dialog";
import { Input } from "@chams-pay/design-system/components/input";
import { Label } from "@chams-pay/design-system/components/label";
import { Check, Search, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";
import { useRef, useState } from "react";
import { casablancaDate, casablancaMonth, initialOf, periodLabel } from "../core/format.js";
import { useObligationAction } from "../core/use-operations.js";
import { studentFormErrorBodyKey, useStudentForm } from "../core/use-students.js";
import { DatePicker } from "../ui/date-picker.js";
import { Amount, Bidi, Count } from "../ui/figures.js";

export function Surface({
  open,
  onOpenChange,
  title,
  description,
  closeLabel,
  testId,
  footer,
  wide = false,
  children,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description?: string;
  readonly closeLabel: string;
  readonly testId?: string;
  readonly footer?: ReactNode;
  readonly wide?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`responsive-action-surface flex max-h-[88dvh] flex-col gap-0 p-0 ${wide ? "sm:max-w-3xl" : "sm:max-w-xl"}`}
        closeLabel={closeLabel}
        {...(testId ? { "data-testid": testId } : {})}
      >
        <DialogHeader className="responsive-action-surface__header border-b border-border/70 p-5">
          <DialogTitle className="font-heading">{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="responsive-action-surface__body flex-1 overflow-y-auto p-5">{children}</div>
        {footer && (
          <div className="responsive-action-surface__footer flex flex-wrap justify-end gap-2 border-t border-border/70 p-4">
            {footer}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* The period grid — one component for capture step 1 and fee adjust   */
/* ------------------------------------------------------------------ */

export type PeriodState = "paid" | "overdue" | "due" | "upcoming";

/** A period is read from the data alone: settled, late, current or ahead. */
export function periodState(
  entry: { readonly period: string; readonly remainingMad: string; readonly overdue: boolean },
  today = casablancaMonth(),
): PeriodState {
  if (entry.remainingMad === "0") return "paid";
  if (entry.overdue) return "overdue";
  if (!/^\d{4}-\d{2}$/.test(entry.period) || entry.period <= today) return "due";
  return "upcoming";
}

const stateTone: Record<PeriodState, string> = {
  paid: "text-muted-2",
  overdue: "text-destructive",
  due: "text-warning",
  upcoming: "text-muted-foreground",
};

export const periodStateKey = {
  paid: "sp.paid",
  overdue: "sp.overdue",
  due: "sp.due",
  upcoming: "sp.upcoming",
} as const;

export function PeriodCell({
  locale,
  period,
  amountMad,
  state,
  selected,
  compact = false,
  onToggle,
}: {
  readonly locale: Locale;
  readonly period: string;
  readonly amountMad: string;
  readonly state: PeriodState;
  readonly selected: boolean;
  readonly compact?: boolean;
  readonly onToggle?: (period: string) => void;
}) {
  const paid = state === "paid";
  return (
    <button
      type="button"
      className={`month-choice relative flex flex-col items-center justify-center gap-1.5 rounded-lg border-[1.5px] border-border bg-card px-2 text-center transition-[background-color,border-color,box-shadow] duration-fast hover:border-teal-300 disabled:cursor-not-allowed disabled:border-border/60 disabled:bg-surface-2 disabled:opacity-60 disabled:hover:border-border/60 data-[selected=true]:border-primary data-[selected=true]:bg-teal-50 data-[selected=true]:ring-3 data-[selected=true]:ring-primary/15 ${
        compact ? "min-h-[74px]" : "min-h-[86px]"
      }`}
      data-selected={selected}
      data-state={state}
      disabled={paid || !onToggle}
      aria-pressed={selected}
      onClick={() => onToggle?.(period)}
    >
      {selected && (
        <span
          aria-hidden="true"
          className="absolute top-1.5 end-1.5 grid size-4 place-items-center rounded-full bg-primary text-primary-foreground"
        >
          <Check className="size-2.5" strokeWidth={3} />
        </span>
      )}
      <span className="font-heading text-[12.5px] font-bold leading-tight">
        {periodLabel(period, locale)}
      </span>
      <b className="font-heading text-[17px] leading-none">
        <Amount value={amountMad} locale={locale} />
      </b>
      <span className={`font-heading text-[10px] font-bold ${stateTone[state]}`}>
        {translate(locale, periodStateKey[state])}
      </span>
    </button>
  );
}

export function PeriodGrid({
  locale,
  entries,
  selected,
  compact = false,
  onToggle,
  label,
}: {
  readonly locale: Locale;
  readonly entries: readonly {
    readonly period: string;
    readonly remainingMad: string;
    readonly overdue: boolean;
  }[];
  readonly selected: ReadonlySet<string>;
  readonly compact?: boolean;
  readonly onToggle?: (period: string) => void;
  readonly label: string;
}) {
  return (
    <div
      className={`month-strip grid gap-2.5 ${compact ? "grid-cols-3" : "grid-cols-2 sm:grid-cols-4"}`}
      role="group"
      aria-label={label}
    >
      {entries.map((entry) => (
        <PeriodCell
          key={entry.period}
          locale={locale}
          period={entry.period}
          amountMad={entry.remainingMad}
          state={periodState(entry)}
          selected={selected.has(entry.period)}
          compact={compact}
          {...(onToggle ? { onToggle } : {})}
        />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Student registration and editing                                    */
/* ------------------------------------------------------------------ */

function ServiceSwitch({
  label,
  priceMad,
  locale,
  checked,
  onChange,
}: {
  readonly label: string;
  readonly priceMad: string | undefined;
  readonly locale: Locale;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex min-h-14 items-center gap-3 bg-card px-3.5">
      <span className="flex flex-col gap-0.5">
        <span className="font-heading text-[12.5px] font-bold">{label}</span>
        {priceMad !== undefined && (
          <small className="text-[10.5px] text-muted-foreground">
            <Amount value={priceMad} locale={locale} /> {translate(locale, "sp.perMonthShort")}
          </small>
        )}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className="ms-auto grid h-11 w-[46px] shrink-0 place-items-center"
      >
        <span
          data-on={checked}
          className="relative block h-[26px] w-[46px] rounded-full bg-border transition-colors duration-base data-[on=true]:bg-primary"
        >
          <span
            className="absolute top-[3px] block size-5 rounded-full bg-white transition-[inset-inline-start] duration-base"
            style={{ insetInlineStart: checked ? "23px" : "3px" }}
          />
        </span>
      </button>
    </div>
  );
}

function DuplicateCard({
  locale,
  match,
  onOpenExisting,
  onSaveAnyway,
}: {
  readonly locale: Locale;
  readonly match: StudentSearchItemContract;
  readonly onOpenExisting: () => void;
  readonly onSaveAnyway: () => void;
}) {
  return (
    <div className="student-form__duplicate animate-shake flex flex-col gap-3 rounded-lg border border-warning-border bg-card p-5 shadow-e3">
      <strong className="font-heading text-[15px]">{translate(locale, "sp.dupTitle")}</strong>
      <p className="text-xs leading-relaxed text-muted-foreground">
        {translate(locale, "sp.dupBody")}
      </p>
      <div className="flex items-center gap-3 rounded-md border border-border/70 bg-surface-2 p-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-md bg-sunken font-heading text-[13px] font-bold text-muted-foreground">
          {initialOf(locale === "ar" ? match.nameAr : match.nameFr)}
        </span>
        <span className="flex min-w-0 flex-col gap-0.5">
          <strong className="truncate font-heading text-[12.5px]">
            {locale === "ar" ? match.nameAr : match.nameFr}
          </strong>
          <small className="truncate text-[10.5px] text-muted-foreground">
            <Bidi>
              {[
                match.massar,
                match.internalReference,
                normalizeSchoolLevelLabel(match.currentClass),
              ]
                .filter(Boolean)
                .join(" · ")}
            </Bidi>
          </small>
        </span>
      </div>
      <div className="flex gap-2.5">
        <Button type="button" className="flex-1" onClick={onOpenExisting}>
          {translate(locale, "sp.openExisting")}
        </Button>
        <Button type="button" variant="outline" onClick={onSaveAnyway}>
          {translate(locale, "sp.saveAnyway")}
        </Button>
      </div>
    </div>
  );
}

export function StudentFormSurface({
  locale,
  csrf,
  open,
  student,
  classOptions = [],
  onOpenChange,
  onSaved,
  onOpenStudent,
}: {
  readonly locale: Locale;
  readonly csrf: string;
  readonly open: boolean;
  readonly student?: StudentProfileContract | undefined;
  readonly classOptions?: readonly string[];
  readonly onOpenChange: (open: boolean) => void;
  readonly onSaved: (profile: StudentProfileContract) => void;
  readonly onOpenStudent?: (id: string) => void;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const form = useStudentForm({
    open,
    csrf,
    student,
    classOptions,
    onSaved,
    onClose: () => onOpenChange(false),
  });
  const advancingRef = useRef(false);
  const advance = () => {
    advancingRef.current = true;
    form.setStep(2);
    window.setTimeout(() => {
      advancingRef.current = false;
    }, 250);
  };
  const prices = form.monthlyAmountsMad;
  const computed = form.computedMonthlyMad;
  const classes = classOptions.includes(form.value.classLabel)
    ? classOptions
    : form.value.classLabel
      ? [form.value.classLabel, ...classOptions]
      : classOptions;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && advancingRef.current) return;
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent
        className="student-form-surface flex max-h-[88dvh] w-full flex-col gap-0 p-0 sm:max-w-[460px]"
        closeLabel={t("common.close")}
        data-testid={form.editing ? "edit-student-surface" : "register-student-surface"}
      >
        <DialogHeader className="border-b border-border/70 px-5 py-4">
          <div className="flex items-center gap-2.5">
            <DialogTitle className="font-heading text-base">
              {t(form.editing ? "sp.editStudent" : "sp.registerStudent")}
            </DialogTitle>
            <span className="ms-auto me-9 font-heading text-[11px] font-bold text-muted-foreground">
              <Count value={form.step} /> / <Count value={2} /> ·{" "}
              {t(form.step === 1 ? "sp.identity" : "sp.enrollment")}
            </span>
          </div>
          <DialogDescription className="sr-only">{t("students.formDescription")}</DialogDescription>
          <ol className="student-form__steps mt-3 flex gap-1.5" aria-hidden="true">
            {([1, 2] as const).map((number) => (
              <li
                key={number}
                data-active={form.step >= number}
                className="h-1 flex-1 rounded-full bg-border transition-colors duration-base data-[active=true]:bg-primary"
              />
            ))}
          </ol>
        </DialogHeader>

        <form
          id="student-action-form"
          className="student-form flex flex-1 flex-col gap-3.5 overflow-y-auto p-5"
          onSubmit={(event) => {
            event.preventDefault();
            if (form.step === 1) {
              advance();
              return;
            }
            void form.save();
          }}
        >
          {form.step === 1 ? (
            <div className="student-form__grid flex animate-fade-up flex-col gap-3.5">
              {!form.editing && form.registrationYears.length > 1 && (
                <Label className="flex flex-col items-stretch gap-1.5 text-start">
                  <span className="text-[11.5px] font-bold text-foreground/70">
                    {t("students.academicYear")}
                  </span>
                  <select
                    className="h-11 rounded-md border border-input bg-card px-3 font-heading text-sm"
                    value={form.value.academicYear ?? ""}
                    onChange={(event) => form.patch({ academicYear: event.target.value })}
                  >
                    {form.registrationYears.map((year) => (
                      <option key={year.academicYear} value={year.academicYear}>
                        {t(year.role === "current" ? "sp.currentYearGroup" : "sp.nextYearGroup")} ·{" "}
                        {year.academicYear}
                      </option>
                    ))}
                  </select>
                </Label>
              )}
              <Label className="flex flex-col items-stretch gap-1.5 text-start">
                <span className="text-[11.5px] font-bold text-foreground/70">{t("sp.nameAr")}</span>
                <Input
                  className="h-11"
                  dir="rtl"
                  required
                  value={form.value.nameAr}
                  onChange={(event) => form.patch({ nameAr: event.target.value })}
                />
              </Label>
              <Label className="flex flex-col items-stretch gap-1.5 text-start">
                <span className="text-[11.5px] font-bold text-foreground/70">{t("sp.nameFr")}</span>
                <Input
                  className="h-11"
                  dir="ltr"
                  required
                  value={form.value.nameFr}
                  onChange={(event) => form.patch({ nameFr: event.target.value })}
                />
              </Label>
              <div className="grid gap-3 sm:grid-cols-[1.3fr_1fr]">
                <Label className="flex flex-col items-stretch gap-1.5 text-start">
                  <span className="text-[11.5px] font-bold text-foreground/70">MASSAR</span>
                  <Input
                    className="chams-figure h-11"
                    dir="ltr"
                    value={form.value.massar ?? ""}
                    aria-busy={form.duplicateBusy}
                    aria-invalid={form.fieldErrors.massar || undefined}
                    aria-describedby={form.fieldErrors.massar ? "desktop-massar-error" : undefined}
                    onChange={(event) => form.patch({ massar: event.target.value || null })}
                    onBlur={() => {
                      form.blurField("massar");
                      void form.checkDuplicate();
                    }}
                  />
                  {form.fieldErrors.massar && (
                    <span
                      id="desktop-massar-error"
                      role="alert"
                      className="text-[10.5px] leading-snug text-destructive"
                    >
                      {t("sp.invalidMassar")}
                    </span>
                  )}
                </Label>
                <Label className="flex flex-col items-stretch gap-1.5 text-start">
                  <span className="text-[11.5px] font-bold text-foreground/70">
                    {t("sp.birthDate")}
                  </span>
                  <DatePicker
                    locale={locale}
                    label={t("sp.birthDate")}
                    value={form.value.birthDate}
                    max={casablancaDate()}
                    invalid={form.fieldErrors.birthDate}
                    describedBy={
                      form.fieldErrors.birthDate ? "desktop-birth-date-error" : undefined
                    }
                    onBlur={() => form.blurField("birthDate")}
                    onChange={(value) => form.patch({ birthDate: value })}
                  />
                  {form.fieldErrors.birthDate && (
                    <span
                      id="desktop-birth-date-error"
                      role="alert"
                      className="text-[10.5px] leading-snug text-destructive"
                    >
                      {t("sp.invalidBirthDate")}
                    </span>
                  )}
                </Label>
              </div>
              <Label className="flex flex-col items-stretch gap-1.5 text-start">
                <span className="text-[11.5px] font-bold text-foreground/70">
                  {t("sp.guardian")}
                </span>
                <Input
                  className="h-11"
                  required
                  value={form.value.guardianName}
                  onChange={(event) => form.patch({ guardianName: event.target.value })}
                />
              </Label>
              <Label className="flex flex-col items-stretch gap-1.5 text-start">
                <span className="text-[11.5px] font-bold text-foreground/70">{t("sp.phone")}</span>
                <Input
                  className="chams-figure h-11"
                  type="tel"
                  dir="ltr"
                  required
                  disabled={form.detailsBusy}
                  placeholder={form.detailsBusy ? t("state.loading") : undefined}
                  value={form.value.guardianPhone ?? ""}
                  aria-busy={form.detailsBusy || undefined}
                  aria-invalid={form.fieldErrors.guardianPhone || undefined}
                  aria-describedby={
                    form.fieldErrors.guardianPhone ? "desktop-guardian-phone-error" : undefined
                  }
                  onChange={(event) => form.patch({ guardianPhone: event.target.value })}
                  onBlur={() => form.blurField("guardianPhone")}
                />
                {form.fieldErrors.guardianPhone && (
                  <span
                    id="desktop-guardian-phone-error"
                    role="alert"
                    className="text-[10.5px] leading-snug text-destructive"
                  >
                    {t("sp.invalidPhone")}
                  </span>
                )}
              </Label>
              {form.duplicate && (
                <DuplicateCard
                  locale={locale}
                  match={form.duplicate}
                  onOpenExisting={() => {
                    const id = form.duplicate?.id;
                    form.dismissDuplicate();
                    onOpenChange(false);
                    if (id) onOpenStudent?.(id);
                  }}
                  onSaveAnyway={form.dismissDuplicate}
                />
              )}
            </div>
          ) : (
            <div className="student-form__grid flex animate-fade-up flex-col gap-3.5">
              <Label className="flex flex-col items-stretch gap-1.5 text-start">
                <span className="text-[11.5px] font-bold text-foreground/70">{t("sp.class")}</span>
                <select
                  className="h-11 rounded-md border border-input bg-card px-3 font-heading text-sm"
                  required
                  value={form.value.classLabel}
                  onChange={(event) => form.patch({ classLabel: event.target.value })}
                >
                  <option value="">—</option>
                  {classes.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </Label>
              {form.sectionRequired && (
                <Label className="flex flex-col items-stretch gap-1.5 text-start">
                  <span className="text-[11.5px] font-bold text-foreground/70">
                    {t("sp.section")}
                  </span>
                  <select
                    data-testid="desktop-student-section"
                    className="h-11 rounded-md border border-input bg-card px-3 font-heading text-sm"
                    required
                    value={form.value.sectionLabel ?? ""}
                    onChange={(event) =>
                      form.patch({ sectionLabel: event.target.value as "A" | "B" })
                    }
                  >
                    <option value="">—</option>
                    {form.sectionOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </Label>
              )}
              <div className="student-form__services flex flex-col divide-y divide-border/70 overflow-hidden rounded-lg border border-border">
                <ServiceSwitch
                  locale={locale}
                  label={t("sp.transport")}
                  priceMad={prices.transport}
                  checked={form.value.transport}
                  onChange={(checked) => form.patch({ transport: checked })}
                />
                <ServiceSwitch
                  locale={locale}
                  label={t("sp.accompaniment")}
                  priceMad={prices.accompaniment}
                  checked={form.value.accompaniment}
                  onChange={(checked) => form.patch({ accompaniment: checked })}
                />
              </div>
              {computed !== null && (
                <div className="flex items-baseline justify-between rounded-md border border-teal-100 bg-teal-50 px-4 py-3">
                  <span className="text-[11.5px] text-primary-strong">
                    {t("sp.computedMonthly")}
                  </span>
                  <strong className="font-heading text-[17px] text-primary-strong">
                    <Amount value={computed} locale={locale} />
                  </strong>
                </div>
              )}
            </div>
          )}

          {form.error && (
            <div
              role="alert"
              className="student-form__failure animate-shake flex flex-col gap-3 rounded-lg border border-destructive-border bg-card p-4"
            >
              <strong className="font-heading text-sm">
                {t(form.error === "duplicate" ? "sp.dupTitle" : "sp.saveFailTitle")}
              </strong>
              <p className="text-xs leading-relaxed text-muted-foreground">
                {t(studentFormErrorBodyKey(form.error, form.errorCode))}
              </p>
              {form.errorCode && (
                <span className="rounded-sm border border-destructive-border bg-destructive-soft px-3 py-2 text-[11px] text-destructive">
                  <Bidi>{form.errorCode}</Bidi>
                </span>
              )}
              <div className="flex gap-2.5">
                <Button type="button" className="flex-1" onClick={form.clearError}>
                  {t("sp.fixAndRetry")}
                </Button>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  {t("sp.keepDraft")}
                </Button>
              </div>
            </div>
          )}
        </form>

        <div className="flex items-center gap-2.5 border-t border-border/70 bg-surface-2 px-5 py-4">
          {form.step === 1 ? (
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("sp.cancel")}
            </Button>
          ) : (
            <Button type="button" variant="outline" onClick={() => form.setStep(1)}>
              {t("sp.back")}
            </Button>
          )}
          {form.step === 1 ? (
            <Button
              type="button"
              className="ms-auto"
              disabled={!form.identityValid}
              onPointerDown={() => {
                advancingRef.current = true;
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                advance();
              }}
            >
              {t("sp.next")}
            </Button>
          ) : (
            <Button
              type="submit"
              form="student-action-form"
              className="ms-auto"
              disabled={form.busy || !form.enrollmentValid}
            >
              {form.busy ? t("state.loading") : t("sp.saveStudent")}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Fee adjustment                                                      */
/* ------------------------------------------------------------------ */

const adjustKinds = [
  { kind: "discount", key: "sp.discount" },
  { kind: "waiver", key: "sp.waiver" },
  { kind: "adjustment", key: "sp.correction" },
] as const;

/** Presentational preview only — the server recomputes the authoritative net. */
function previewRemaining(
  remainingMad: string,
  kind: ObligationActionKindContract,
  amount: string,
): string | null {
  if (kind === "waiver") return "0";
  if (!/^-?\d+$/.test(amount.trim())) return null;
  const delta = BigInt(amount.trim());
  const next = kind === "discount" ? BigInt(remainingMad) - delta : BigInt(remainingMad) + delta;
  return (next < 0n ? 0n : next).toString();
}

export function ObligationActionControl({
  locale,
  obligation,
  csrf,
  studentName,
  onSaved,
}: {
  readonly locale: Locale;
  readonly obligation: ObligationContract;
  readonly csrf: string;
  readonly studentName?: string;
  readonly onSaved: (result: ObligationActionResultContract) => void;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const text = operationsMessages(locale);
  const [open, setOpen] = useState(false);
  const action = useObligationAction({
    obligation,
    csrf,
    onSaved,
    onClose: () => setOpen(false),
  });
  const formId = `obligation-action-${obligation.id}`;
  const label = locale === "ar" ? obligation.labelAr : obligation.labelFr;
  const preview = previewRemaining(obligation.remainingMad, action.kind, action.amount);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="obligation-action-trigger h-11 min-h-11 px-3 text-[11px]"
        aria-label={`${t("sp.adjust")} — ${label}`}
        onClick={() => setOpen(true)}
      >
        {t("sp.adjust")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="adjust-fee-surface flex max-h-[88dvh] w-full flex-col gap-0 p-0 sm:max-w-[470px]"
          closeLabel={text.close}
          data-testid="obligation-action-surface"
        >
          <DialogHeader className="border-b border-border/70 px-5 py-4">
            <div className="flex items-center gap-2.5">
              <DialogTitle className="font-heading text-base">{t("sp.adjustFee")}</DialogTitle>
              <DialogDescription className="ms-auto me-9 text-[11px] font-bold text-muted-foreground">
                {periodLabel(obligation.period, locale)}
                {studentName ? ` · ${studentName}` : ""}
              </DialogDescription>
            </div>
          </DialogHeader>

          <form
            id={formId}
            className="operations-form flex flex-1 flex-col gap-4 overflow-y-auto p-5"
            onSubmit={(event) => {
              event.preventDefault();
              void action.submit();
            }}
          >
            <div
              className="flex gap-[3px] rounded-md bg-muted p-[3px]"
              role="radiogroup"
              aria-label={text.obligationActionTitle}
            >
              {adjustKinds.map((entry) => (
                <button
                  key={entry.kind}
                  type="button"
                  role="radio"
                  aria-checked={action.kind === entry.kind}
                  data-active={action.kind === entry.kind}
                  onClick={() => action.setKind(entry.kind)}
                  className="grid h-11 flex-1 place-items-center rounded-sm font-heading text-xs font-bold text-muted-foreground transition-colors duration-base data-[active=true]:bg-card data-[active=true]:text-foreground data-[active=true]:shadow-e1"
                >
                  {t(entry.key)}
                </button>
              ))}
            </div>

            <div className="obligation-action-context grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5 rounded-md border border-border/70 bg-surface-2 px-4 py-3">
                <span className="chams-eyebrow">{t("sp.originalAmount")}</span>
                <strong className="font-heading text-[17px] text-muted-foreground">
                  <Amount value={obligation.remainingMad} locale={locale} />
                </strong>
              </div>
              <div className="flex flex-col gap-1.5 rounded-md border border-teal-100 bg-teal-50 px-4 py-3">
                <span className="chams-eyebrow text-primary-strong/70">{t("sp.newRemaining")}</span>
                <strong className="font-heading text-[17px] text-primary-strong">
                  {preview === null ? "—" : <Amount value={preview} locale={locale} />}
                </strong>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {action.kind !== "waiver" && (
                <Label className="flex flex-col items-stretch gap-1.5 text-start">
                  <span className="text-[11.5px] font-bold text-foreground/70">
                    {t("sp.adjustAmount")}
                  </span>
                  <Input
                    className="chams-figure h-11"
                    type="number"
                    step="1"
                    inputMode="numeric"
                    dir="ltr"
                    required
                    value={action.amount}
                    onChange={(event) => action.setAmount(event.target.value)}
                  />
                </Label>
              )}
              <Label className="flex flex-col items-stretch gap-1.5 text-start">
                <span className="text-[11.5px] font-bold text-foreground/70">
                  {t("sp.effectiveDate")}
                </span>
                <DatePicker
                  locale={locale}
                  label={t("sp.effectiveDate")}
                  max={casablancaDate()}
                  value={action.effectiveDate}
                  onChange={action.setEffectiveDate}
                />
              </Label>
            </div>

            <Label className="flex flex-col items-stretch gap-1.5 text-start">
              <span className="text-[11.5px] font-bold text-foreground/70">
                {t("sp.reason")} <span className="text-destructive">*</span>
              </span>
              <textarea
                className="min-h-20 rounded-md border border-input bg-card px-3 py-2.5 text-[12.5px] leading-relaxed"
                rows={3}
                required
                minLength={3}
                maxLength={500}
                value={action.reason}
                onChange={(event) => action.setReason(event.target.value)}
              />
            </Label>

            <p className="flex items-start gap-2.5 rounded-md border border-warning-border bg-warning-soft px-4 py-3 text-[11.5px] leading-relaxed text-warning">
              <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
              {t("sp.adjustWarn")}
            </p>

            {action.failed && (
              <p
                role="alert"
                className="animate-shake rounded-md border border-destructive-border bg-destructive-soft px-4 py-3 text-xs text-destructive"
              >
                {text.actionFailed}
              </p>
            )}
          </form>

          <div className="operations-surface-actions flex items-center gap-2.5 border-t border-border/70 bg-surface-2 px-5 py-4">
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t("sp.cancel")}
            </Button>
            <Button
              type="submit"
              form={formId}
              className="ms-auto"
              disabled={action.submitDisabled}
            >
              {t("sp.applyAdjust")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Empty search state                                                  */
/* ------------------------------------------------------------------ */

export function StudentsEmptyState({
  locale,
  filtered = false,
  action,
}: {
  readonly locale: Locale;
  readonly filtered?: boolean;
  readonly action?: ReactNode;
}) {
  return (
    <div className="empty-state flex flex-col items-center gap-3 px-6 py-12 text-center">
      <span className="grid size-[46px] place-items-center rounded-full bg-surface-2">
        <Search aria-hidden="true" className="size-5 text-muted-2" strokeWidth={1.7} />
      </span>
      <strong className="font-heading text-sm">{translate(locale, "sp.emptyTitle")}</strong>
      <p className="max-w-[38ch] text-xs leading-relaxed text-muted-foreground">
        {translate(locale, filtered ? "sp.filteredEmptyBody" : "sp.emptyBody")}
      </p>
      {action}
    </div>
  );
}
